'use client'

import React, { useEffect, useState } from 'react'
import { cn } from '@/utilities/ui'

const SCROLL_OFFSET = 140

export type TableOfContentsBlockProps = {
  blockType: 'tableOfContents'
  blockName?: string
}

type Heading = {
  id: string
  text: string
  level: number
}

export const TableOfContentsBlock: React.FC<TableOfContentsBlockProps> = ({ blockName }) => {
  const [headings, setHeadings] = useState<Heading[]>([])
  const [activeId, setActiveId] = useState<string>('')

  useEffect(() => {
    // Extract all headings from the page (h2, h3)
    const extractHeadings = () => {
      const elements = document.querySelectorAll('h2[id], h3[id]')
      const extracted: Heading[] = Array.from(elements).map((el) => ({
        id: el.id,
        text: el.textContent || '',
        level: parseInt(el.tagName.substring(1)),
      }))
      setHeadings(extracted)
    }

    // Extract headings after initial render
    extractHeadings()

    // Re-extract if content changes (for dynamic content)
    const observer = new MutationObserver(extractHeadings)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    // Track active heading on scroll
    const handleScroll = () => {
      const scrollPosition = window.scrollY + SCROLL_OFFSET // Offset for header

      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i]
        if (!heading) continue

        const element = document.getElementById(heading.id)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveId(heading.id)
          return
        }
      }
      setActiveId('')
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check

    return () => window.removeEventListener('scroll', handleScroll)
  }, [headings])

  if (headings.length === 0) {
    return null
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const element = document.getElementById(id)
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      window.scrollTo({
        top: elementPosition - SCROLL_OFFSET,
        behavior: 'smooth',
      })
      // Update URL hash
      window.history.pushState(null, '', `#${id}`)
    }
  }

  return (
    <nav
      className={cn('my-8 p-6 rounded-lg border border-border bg-card', 'lg:my-12')}
      aria-label="Table of contents"
    >
      <h2 className="text-lg font-semibold mb-4 text-foreground">
        {blockName || 'Table of Contents'}
      </h2>
      <ul className="space-y-2 list-none">
        {headings.map((heading) => (
          <li
            key={heading.id}
            className={cn(
              heading.level === 3 && 'ml-4',
              'transition-all duration-200 flex items-start',
            )}
          >
            <span
              className={cn(
                'mr-2 mt-0.5 flex-shrink-0 text-2xl font-bold leading-none',
                activeId === heading.id ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              •
            </span>
            <a
              href={`#${heading.id}`}
              onClick={(e) => handleClick(e, heading.id)}
              className={cn(
                'block py-1 rounded text-sm transition-all duration-200 flex-1',
                'hover:text-primary hover:translate-x-1',
                activeId === heading.id ? 'text-primary font-medium' : 'text-muted-foreground',
              )}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default TableOfContentsBlock